import moment from 'moment';

interface ICacheStorage {
  has: (key: string) => boolean;
  get: (key: string) => CacheObjectType | string;
  set: (key: string, value: unknown) => void;
  delete: (key: string) => boolean;
  clear: () => void;
}

export interface ICacheServiceOptions {
  storage?: ICacheStorage;
  defaultMaxAgeSeconds?: number;
  storeValueSerialized?: boolean;
}

type CacheObjectType = {
  value: unknown;
  createdAt: number;
  expiredAt: number;
};

/**
 * The cache service provides with basic methods for handling the caching process.
 * Default cache storage is the js native Map, that can be replaced with another by passing the instance to the constructor,
 * The external storage instance must comply with the ICacheStorage interface
 */
export class CacheService {
  private storage: ICacheStorage;
  private defaultMaxAgeSeconds;
  private storeValueSerialized;

  constructor({ storage, defaultMaxAgeSeconds = 60, storeValueSerialized = false }: ICacheServiceOptions) {
    this.storage = storage || new Map();
    this.defaultMaxAgeSeconds = defaultMaxAgeSeconds;
    this.storeValueSerialized = storeValueSerialized;
  }

  public get(key: string): CacheObjectType {
    if (this.has(key)) {
      return this.storeValueSerialized ? JSON.parse(this.storage.get(key) as string) : this.storage.get(key);
    }
  }

  public getValue(key: string): unknown {
    return this.get(key)?.value;
  }

  public has(key: string): boolean {
    return this.storage.has(key);
  }

  public set(key: string, value: unknown, maxAgeSeconds?: number) {
    if (typeof value === 'undefined') {
      return;
    }

    const ttl = maxAgeSeconds || this.defaultMaxAgeSeconds;

    const createdAt = moment();
    const expiredAt = createdAt.clone().add(ttl, 'seconds');

    const cachedValue = {
      value,
      createdAt: createdAt.unix(),
      expiredAt: expiredAt.unix(),
    };

    const storedValue = this.storeValueSerialized ? JSON.stringify(cachedValue) : cachedValue;

    this.storage.set(key, storedValue);
  }

  public isValid(key: string): boolean {
    if (!this.has(key)) {
      return false;
    }

    const { expiredAt } = this.get(key);

    return moment.unix(expiredAt).diff(moment()) > 0;
  }

  public delete(key: string) {
    if (this.has(key)) {
      return this.storage.delete(key);
    }

    return false;
  }

  public clear() {
    return this.storage.clear();
  }
}
